import { bowelDays, bowelStats, movementsOn } from './bowel';
import { addDays, weekDays, weekEnd, weekStart } from './dates';
import { fibreByDay, fibreStats } from './fibre';
import { firstLogDate } from './logs';
import { symptomDays, symptomNames, symptomStats } from './symptoms';
import { waterByDay, waterStats } from './water';

const NO_FIBRE = { target: undefined, months: {}, days: [], unavailable: false, water: { months: {}, days: [], unavailable: false } };

/** A span's fibre status: an error or a loading day (or target) wins over ready. */
function spanStatus(ctx, days) {
  const all = days.map(ctx.fibreStatus);
  if (all.includes('error')) return 'error';
  if (ctx.fibreTarget === undefined || all.includes('loading')) return 'loading';
  return 'ready';
}

/** A span's water status: an error or a loading day wins over ready. */
function waterSpanStatus(ctx, days) {
  const all = days.map(ctx.waterStatus);
  if (all.includes('error')) return 'error';
  if (all.includes('loading')) return 'loading';
  return 'ready';
}

/**
 * Merges the three sources into per-day maps and per-week stats — the one
 * object every view model reads.
 *
 * @param {{ today: string, historyStart: string, symptoms: Array, bowel: Array }} data - From useGutData.
 * @param {{ bristolLookup?: (id: any) => number|null, fibre?: ReturnType<import('../api/useGutFibre').useGutFibre> }} [opts]
 *   Without `fibre`, every fibre day reads as loading (the context is then only used to work out which months to fetch).
 */
export function buildContext(data, { bristolLookup, fibre = NO_FIBRE } = {}) {
  const { today, historyStart } = data;
  const sym = symptomDays(data.symptoms);
  const firstLog = firstLogDate(data);
  const inRange = (iso) => iso >= historyStart && iso <= today;
  const ctx = {
    today,
    historyStart,
    minWeek: weekStart(historyStart),
    maxWeek: weekStart(today),
    sym,
    names: symptomNames(sym),
    bowel: bowelDays(data.bowel, bristolLookup),
    fibre: fibreByDay(fibre.days),
    // Undefined while the target is loading; null when none is set.
    fibreTarget: fibre.target,
    // `{ [iso]: { ml, plan } }`, logged days only; each day has its own plan.
    water: waterByDay(fibre.water?.days),
    firstLog,
    firstWeek: firstLog ? weekStart(firstLog) : null,
    hasData: Boolean(firstLog),
    inRange,
  };

  /** Whether a day's fibre is loaded: 'ready', 'loading' or 'error' (request failed, or no diet event type). */
  ctx.fibreStatus = (iso) => (fibre.unavailable ? 'error' : fibre.months[iso.slice(0, 7)] || 'loading');
  /** The same for water: 'ready', 'loading' or 'error' (request failed, or no profile event type). */
  const water = fibre.water || NO_FIBRE.water;
  ctx.waterStatus = (iso) => (water.unavailable ? 'error' : water.months[iso.slice(0, 7)] || 'loading');

  const weeks = new Map();
  /** Stats for the week starting `ws`, over its days inside the fetched window. */
  ctx.week = (ws) => {
    if (!weeks.has(ws)) weeks.set(ws, ctx.span(ws, weekEnd(ws)));
    return weeks.get(ws);
  };
  /** Stats over any span [start, end], clipped to the fetched window. */
  ctx.span = (start, end) => {
    const days = [];
    for (let d = start; d <= end; d = addDays(d, 1)) if (inRange(d)) days.push(d);
    return {
      start,
      end,
      days,
      sym: symptomStats(sym, days, ctx.names),
      bowel: bowelStats(movementsOn(ctx.bowel, days)),
      fibre: { ...fibreStats(ctx.fibre, days, ctx.fibreTarget), status: spanStatus(ctx, days) },
      water: { ...waterStats(ctx.water, days), status: waterSpanStatus(ctx, days) },
    };
  };
  ctx.weekDays = (ws) => weekDays(ws).filter(inRange);
  return ctx;
}
